import { DatePicker, FormField, SelectSingle, TextArea as DsTextArea, TextInput as DsTextInput, Toggle } from '@orbitalwatch/design';
import { DateTime } from 'luxon';
import { forwardRef, useId, useMemo } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useFieldControl } from './formControl';

/* ------------------------------------------------------------------ Field */

export interface FieldProps {
    label: string;
    /** 12 px text-3 help line under the control; replaced by `error` while there is one. */
    hint?: ReactNode;
    error?: string | null;
    /** Adds " *" to the label. */
    required?: boolean;
    disabled?: boolean;
    children?: ReactNode;
}

/**
 * Library `FormField` (real `<label for>`, hint, a reserved message line) fed by a synthesised `IFormControl`
 * (`formControl.ts`). The library controls inside take their id, error and disabled state from the field.
 */
export function Field(props: FieldProps) {
    const formControl = useFieldControl({ error: props.error, disabled: props.disabled });
    return (
        <FormField label={props.required ? `${props.label} *` : props.label} hint={props.hint} formControl={formControl}>
            {props.children}
        </FormField>
    );
}

/* ------------------------------------------------------------------ ReadOnlyValue */

const READ_ONLY_CONTROL = { error: null, disabled: false };

/** A value that cannot be edited here (Parent, Key, Path in drawers): field label over a surface-2 mono box. */
export function ReadOnlyValue(props: { label: string; hint?: ReactNode; children: ReactNode }) {
    const formControl = useFieldControl(READ_ONLY_CONTROL);
    const id = `ark-readonly-${useId().replace(/:/g, '')}`;
    return (
        <FormField label={props.label} hint={props.hint} formControl={formControl} controlId={id}>
            {/* Out of the tab order: the drawer focus trap would otherwise land here instead of on the first input. */}
            <div
                id={id}
                className="ark-readonly"
                role="textbox"
                aria-readonly="true"
                tabIndex={-1}
                aria-labelledby={`${id}-label`}
                aria-describedby={props.hint ? `${id}-message` : undefined}
            >
                {props.children}
            </div>
        </FormField>
    );
}

/* ------------------------------------------------------------------ TextInput */

export interface TextInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    type?: 'text' | 'email' | 'number' | 'password' | 'search';
    disabled?: boolean;
    hasError?: boolean;
    autoFocus?: boolean;
    className?: string;
    /** Leading 16 px icon. */
    icon?: ReactNode;
    onKeyDown?: (ev: KeyboardEvent<HTMLInputElement>) => void;
    /** Name when there is no `Field` label around it (a filter box). */
    'aria-label'?: string;
    maxLength?: number;
}

/** Library `TextInput` (value-callback contract; forwards its ref to the native `<input>`). */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, ref) {
    return (
        <DsTextInput
            ref={ref}
            value={props.value}
            onChange={props.onChange}
            placeholder={props.placeholder}
            type={props.type}
            isDisabled={props.disabled}
            hasError={props.hasError}
            autoFocus={props.autoFocus}
            className={props.className}
            icon={props.icon}
            onKeyDown={props.onKeyDown}
            aria-label={props['aria-label']}
            maxLength={props.maxLength}
        />
    );
});

/* ------------------------------------------------------------------ DateInput */

export interface DateInputProps {
    /** ISO date (`yyyy-MM-dd`) or empty string. */
    value: string;
    onChange: (value: string) => void;
    min?: string;
    max?: string;
    disabled?: boolean;
    hasError?: boolean;
    placeholder?: string;
}

function toDateTime(value: string | undefined): DateTime | undefined {
    if (!value) return undefined;
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : undefined;
}

/** Library `DatePicker` (luxon calendar) bridged to the screens' ISO-date strings. */
export function DateInput(props: DateInputProps) {
    return (
        <DatePicker
            value={toDateTime(props.value)}
            onChange={date => props.onChange(date?.toISODate() ?? '')}
            minDate={toDateTime(props.min)}
            maxDate={toDateTime(props.max)}
            isDisabled={props.disabled}
            hasError={props.hasError}
            placeholder={props.placeholder ?? 'dd/mm/yyyy'}
        />
    );
}

/* ------------------------------------------------------------------ Select */

export interface SelectOption {
    value: string;
    label: string;
    /** Listed but not selectable. */
    disabled?: boolean;
}

export interface SelectProps {
    options: SelectOption[];
    /** Selected `value`; `''` = nothing selected. */
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean;
}

const labelOf = (option: SelectOption) => option.label;
const idOf = (option: SelectOption) => option.value;
const isOptionDisabled = (option: SelectOption) => !!option.disabled;

/** Library `SelectSingle` (searchable combobox) over `{ value, label }` options. */
export function Select(props: SelectProps) {
    const { options, value } = props;
    const selected = useMemo(() => options.find(option => option.value === value), [options, value]);

    return (
        <SelectSingle<SelectOption>
            options={options}
            value={selected}
            labelSelector={labelOf}
            idSelector={idOf}
            disabledSelector={isOptionDisabled}
            placeholder={props.placeholder ?? 'Select…'}
            isDisabled={props.disabled}
            hasError={props.hasError}
            onChange={option => props.onChange(option ? option.value : '')}
        />
    );
}

/* ------------------------------------------------------------------ TextArea */

export interface TextAreaProps {
    value: string;
    onChange: (value: string) => void;
    rows?: number;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean;
    autoFocus?: boolean;
    maxLength?: number;
}

/** Library `TextArea` (same contract as `TextInput`). */
export function TextArea(props: TextAreaProps) {
    return (
        <DsTextArea
            value={props.value}
            onChange={props.onChange}
            rows={props.rows ?? 3}
            placeholder={props.placeholder}
            isDisabled={props.disabled}
            hasError={props.hasError}
            autoFocus={props.autoFocus}
            maxLength={props.maxLength}
        />
    );
}

/* ------------------------------------------------------------------ Checkbox */

export interface CheckboxProps {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

/**
 * Library `Toggle` (`button[role=switch]`) with its label inline (the library `FormFieldToggle` stacks the label
 * above, which does not fit a panel head or a toolbar). The `<label>` names the switch and toggles it on click.
 */
export function Checkbox(props: CheckboxProps) {
    return (
        <label className={props.disabled ? 'ds-toggle is-disabled' : 'ds-toggle'}>
            <Toggle value={props.checked} isDisabled={props.disabled} onToggleChanged={props.onChange} />
            <span>{props.label}</span>
        </label>
    );
}
